import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ClearRounded, GetAppRounded } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { getProjectDocumentRevisionCoversheets } from '../../api/project-document-revisions';
import { ProjectDocumentRevisionFile } from '../../pages/ProjectDocumentRevisionFile/projectDocumentRevisionFileSlice';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 2, 1, 3)
    },
    dialogAction: {
      paddingTop: theme.spacing(1),
      '& > *': {
        width: `calc(100% - ${theme.spacing(2)}px)`,
        margin: theme.spacing(1),
        marginTop: 0
      }
    },
    fileNotFound: {
      padding: theme.spacing(1.5, 2),
      textAlign: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    alertBox: {
      margin: theme.spacing(0, -1, 1, -1)
    }
  })
);

interface DownloadFileProps {
  open: boolean;
  onClose: () => void;
  documentRevisionId: number;
  renderDocumentRevisionFiles?: ProjectDocumentRevisionFile[];
  coversheetName?: string;
}

export default function DownloadFile({
  open,
  onClose,
  documentRevisionId,
  renderDocumentRevisionFiles,
  coversheetName
}: DownloadFileProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const publicFiles = renderDocumentRevisionFiles?.filter(
    (file) => file.isPublic
  );

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <div className={classes.dialogTitle}>
        <Typography className={classes.flexGrow} variant="h6">
          {t('downloadFile.title')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <ClearRounded />
        </IconButton>
      </div>
      <DialogContent dividers>
        <Alert className={classes.alertBox} severity="info">
          {t('downloadFile.onlyPublicFiles')}
        </Alert>
        {publicFiles && publicFiles.length > 0 ? (
          publicFiles.map((file) => {
            const [currentVersion] = file.projectDocumentVersionFiles;
            return (
              <div key={file.id}>
                <Typography variant="body1">{file.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('downloadFile.version')} {currentVersion.versionNo}
                </Typography>
              </div>
            );
          })
        ) : (
          <div className={classes.fileNotFound}>{t('downloadFile.noFileSelected')}</div>
        )}
      </DialogContent>
      <div className={classes.dialogAction}>
        {coversheetName && (
          <Button
            color="primary"
            variant="outlined"
            fullWidth
            startIcon={<GetAppRounded />}
            onClick={() =>
              getProjectDocumentRevisionCoversheets({
                projectDocumentRevisionId: documentRevisionId,
                filename: coversheetName
              })
            }
          >
{t('downloadFile.downloadCoversheet')}
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          fullWidth
          startIcon={<GetAppRounded />}
          disabled
        >
{t('downloadFile.downloadFilesWithCoversheet')}
        </Button>
      </div>
    </Dialog>
  );
}
